import React, { useEffect, useRef, useState } from 'react';
import { formatRemainingDuration, ROOM_DISPLAY_PHASE } from '../useRoomPhase';

const RESPONSIVE_MODE = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
};

const phaseSteps = [
  { key: 'betready', label: '準備' },
  { key: 'betTime', label: 'ベット' },
  { key: 'locked', label: '結果入力' },
  { key: 'resultReview', label: '結果確認' },
  { key: 'confirmed', label: '結果発表' },
];

const ownerConfirmationPhaseSteps = [
  { key: 'betready', label: '準備' },
  { key: 'betTime', label: 'ベット' },
  { key: 'locked', label: '結果登録' },
  { key: 'confirmed', label: '結果発表' },
];

const phaseMeta = {
  [ROOM_DISPLAY_PHASE.INACTIVE]: { icon: '⏳', label: '準備中', stepKey: 'betready' },
  [ROOM_DISPLAY_PHASE.BETREADY]: { icon: '⏳', label: '準備中', stepKey: 'betready' },
  [ROOM_DISPLAY_PHASE.BET_TIME]: { icon: '🔥', label: 'ベット受付中', stepKey: 'betTime' },
  [ROOM_DISPLAY_PHASE.LOCKED]: { icon: '📝', label: '結果入力中', stepKey: 'locked' },
  [ROOM_DISPLAY_PHASE.BET_COMPLETION]: { icon: '🔎', label: '結果確認中', stepKey: 'resultReview' },
  [ROOM_DISPLAY_PHASE.RESULT_PENDING]: { icon: '🔎', label: '結果確認中', stepKey: 'resultReview' },
  [ROOM_DISPLAY_PHASE.CONFIRMED]: { icon: '🏁', label: '結果発表', stepKey: 'confirmed' },
  [ROOM_DISPLAY_PHASE.COMPLETED]: { icon: '✅', label: 'ルーム終了', stepKey: null },
};

const phaseStyle = {
  betready: {
    backgroundColor: '#f3f4f6',
    border: '2px solid #d1d5db',
    color: '#6b7280',
    boxShadow: 'none',
  },
  betTime: {
    backgroundColor: '#fff7e6',
    border: '2px solid #f59e0b',
    color: '#333333',
    boxShadow: '0 0 8px rgba(245, 158, 11, 0.3)',
  },
  locked: {
    backgroundColor: '#eff6ff',
    border: '2px solid #60a5fa',
    color: '#1e3a8a',
    boxShadow: '0 0 8px rgba(96, 165, 250, 0.22)',
  },
  resultReview: {
    backgroundColor: '#eef2ff',
    border: '2px solid #818cf8',
    color: '#312e81',
    boxShadow: '0 0 8px rgba(129, 140, 248, 0.22)',
  },
  confirmed: {
    backgroundColor: '#ecfdf5',
    border: '2px solid #34d399',
    color: '#065f46',
    boxShadow: '0 0 8px rgba(52, 211, 153, 0.22)',
  },
  deleted: {
    backgroundColor: '#f5f5f5',
    border: '2px solid #bdbdbd',
    color: '#616161',
    boxShadow: 'none',
  },
};

const PROGRESS_PHASES = new Set([
  ROOM_DISPLAY_PHASE.BET_TIME,
]);

const isOwnerConfirmation = (event) => event?.result_confirmation_method === 'owner_confirmation';

const getPhaseSteps = (event) => (
  isOwnerConfirmation(event) ? ownerConfirmationPhaseSteps : phaseSteps
);

const getPhaseMeta = (roomDisplayPhase, event) => {
  const meta = phaseMeta[roomDisplayPhase] ?? phaseMeta[ROOM_DISPLAY_PHASE.BETREADY];

  if (!isOwnerConfirmation(event)) {
    return meta;
  }

  if (roomDisplayPhase === ROOM_DISPLAY_PHASE.LOCKED) {
    return { ...meta, label: '結果登録中' };
  }

  if (meta.stepKey === 'resultReview') {
    return { ...meta, stepKey: 'locked' };
  }

  return meta;
};

const getPhaseStyle = (meta) => {
  if (!meta.stepKey) {
    return phaseStyle.deleted;
  }

  return phaseStyle[meta.stepKey] ?? phaseStyle.betready;
};

const getTimerLevel = (seconds, roomDisplayPhase) => {
  if (
    seconds === null ||
    roomDisplayPhase === ROOM_DISPLAY_PHASE.CONFIRMED ||
    roomDisplayPhase === ROOM_DISPLAY_PHASE.COMPLETED
  ) {
    return 'normal';
  }

  if (seconds <= 3) return 'critical';
  if (seconds <= 10) return 'warning';
  return 'normal';
};

const getTimerStyle = (level, defaultColor) => {
  const levelStyle = {
    normal: { fontWeight: 700, fontSize: '16px', color: defaultColor, letterSpacing: 'normal' },
    warning: { fontWeight: 800, fontSize: '20px', color: '#d97706', letterSpacing: 'normal' },
    critical: { fontWeight: 900, fontSize: '24px', color: '#dc2626', letterSpacing: '0.02em' },
  };

  return {
    ...levelStyle[level],
    transition: 'all 0.2s ease',
  };
};

const getProgressColor = (timerEmphasisLevel) => {
  if (timerEmphasisLevel === 'critical') return '#dc2626';
  if (timerEmphasisLevel === 'warning') return '#f59e0b';
  return '#16a34a';
};

const formatBetStartTime = (starttime, now) => {
  if (!starttime || Number.isNaN(starttime.getTime())) {
    return null;
  }

  const time = starttime.toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const isSameDate = now &&
    starttime.getFullYear() === now.getFullYear() &&
    starttime.getMonth() === now.getMonth() &&
    starttime.getDate() === now.getDate();

  if (isSameDate) {
    return time;
  }

  const date = starttime.toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
  });

  return `${date} ${time}`;
};

function PhaseStepper({ event, roomDisplayPhase, isDesktop }) {
  const meta = getPhaseMeta(roomDisplayPhase, event);
  const steps = getPhaseSteps(event);
  if (!meta.stepKey) {
    return null;
  }

  const currentIndex = Math.max(steps.findIndex((step) => step.key === meta.stepKey), 0);

  return (
    <div
      aria-label="ラウンド進行状況"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`,
        gap: isDesktop ? '8px' : '4px',
        alignItems: 'start',
        margin: '6px 0 8px',
      }}
    >
      {steps.map((step, index) => {
        const isDone = index < currentIndex;
        const isCurrent = index === currentIndex;
        const dotColor = isCurrent ? '#2563eb' : isDone ? '#16a34a' : '#cbd5e1';
        const textColor = isCurrent ? '#1d4ed8' : isDone ? '#166534' : '#64748b';

        return (
          <div
            key={step.key}
            style={{
              minWidth: 0,
              display: 'grid',
              gridTemplateRows: '12px auto',
              rowGap: '4px',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: index === steps.length - 1 ? '1fr' : '1fr minmax(0, 1fr)',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  justifySelf: 'center',
                  width: isCurrent ? '12px' : '9px',
                  height: isCurrent ? '12px' : '9px',
                  borderRadius: '999px',
                  backgroundColor: dotColor,
                  boxShadow: isCurrent ? '0 0 0 4px rgba(37, 99, 235, 0.14)' : 'none',
                }}
              />
              {index < steps.length - 1 && (
                <span
                  style={{
                    height: '2px',
                    backgroundColor: index < currentIndex ? '#86efac' : '#e2e8f0',
                    transform: 'translateX(-1px)',
                  }}
                />
              )}
            </div>
            <span
              style={{
                minWidth: 0,
                color: textColor,
                fontSize: isDesktop ? '11px' : '10px',
                fontWeight: isCurrent ? 900 : 800,
                lineHeight: 1.15,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
              }}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function getTimerInfo(round, now, isManualBetClose, roomDisplayPhase) {
  if (roomDisplayPhase === ROOM_DISPLAY_PHASE.COMPLETED) {
    return {
      message: 'ルーム終了',
      referenceMessage: null,
      remainingSeconds: null,
      progressRatio: null,
    };
  }

  if (roomDisplayPhase === ROOM_DISPLAY_PHASE.CONFIRMED) {
    return {
      message: '結果発表中',
      referenceMessage: null,
      remainingSeconds: null,
      progressRatio: null,
    };
  }

  const starttime = round?.starttime ? new Date(round.starttime) : null;
  const endtime = round?.endtime ? new Date(round.endtime) : null;

  if (isManualBetClose && roomDisplayPhase === ROOM_DISPLAY_PHASE.BET_TIME) {
    const betStartTime = formatBetStartTime(starttime, now);

    return {
      message: null,
      referenceMessage: betStartTime ? `ベット開始 ${betStartTime}` : null,
      remainingSeconds: null,
      progressRatio: null,
    };
  }

  if (!starttime || !endtime || Number.isNaN(starttime.getTime()) || Number.isNaN(endtime.getTime())) {
    return {
      message: '次ラウンド開始待ち',
      referenceMessage: null,
      remainingSeconds: null,
      progressRatio: null,
    };
  }

  if (now < starttime) {
    const remainingSeconds = Math.max((starttime.getTime() - now.getTime()) / 1000, 0);
    return {
      message: `次ラウンドのベット開始まであと ${formatRemainingDuration(remainingSeconds)}`,
      referenceMessage: null,
      remainingSeconds,
      progressRatio: null,
    };
  }

  if (isManualBetClose && roomDisplayPhase !== ROOM_DISPLAY_PHASE.BET_TIME) {
    return {
      message: 'ベット終了',
      referenceMessage: null,
      remainingSeconds: 0,
      progressRatio: null,
    };
  }

  if (now < endtime) {
    const remainingSeconds = Math.max((endtime.getTime() - now.getTime()) / 1000, 0);
    const totalSeconds = Math.max((endtime.getTime() - starttime.getTime()) / 1000, 1);
    return {
      message: `ベット終了まであと ${formatRemainingDuration(remainingSeconds)}`,
      referenceMessage: null,
      remainingSeconds,
      progressRatio: Math.min(Math.max(remainingSeconds / totalSeconds, 0), 1),
    };
  }

  return {
    message: 'ベット終了',
    referenceMessage: null,
    remainingSeconds: 0,
    progressRatio: null,
  };
}

function RoundPhase({ event, responsiveMode, round, roomDisplayPhase, userType, updateRound }) {
  const [now, setNow] = useState(() => new Date());
  const counterRef = useRef(0);
  const effectiveDisplayPhase = roomDisplayPhase ?? ROOM_DISPLAY_PHASE.BETREADY;
  const meta = getPhaseMeta(effectiveDisplayPhase, event);
  const currentStyle = getPhaseStyle(meta);
  const timerInfo = getTimerInfo(
    round,
    now,
    event?.bet_close_mode === 'manual',
    effectiveDisplayPhase
  );
  const timerEmphasisLevel = getTimerLevel(timerInfo.remainingSeconds, effectiveDisplayPhase);
  const timerStyle = getTimerStyle(timerEmphasisLevel, currentStyle.color);
  const isDesktop = responsiveMode === RESPONSIVE_MODE.DESKTOP;
  const shouldShowProgress = PROGRESS_PHASES.has(effectiveDisplayPhase) && timerInfo.progressRatio !== null;

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNow(new Date());

      if (!round?.starttime && userType === '' && counterRef.current % 10 === 0) {
        updateRound?.();
      }

      counterRef.current += 1;
    }, 100);

    return () => clearInterval(intervalId);
  }, [round?.starttime, updateRound, userType]);

  const title = event?.name ? `${event.name}　第${event.round}ラウンド` : '';
  const containerStyle = { width: '100%' };
  const titleStyle = {
    margin: isDesktop ? '6px 12px 0' : '6px calc(12px + 2em) 0 12px',
    textAlign: 'center',
    fontSize: isDesktop ? '18px' : '16px',
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#111827',
  };
  const cardStyle = {
    ...currentStyle,
    borderRadius: '12px',
    padding: '10px 14px',
    margin: isDesktop ? '6px auto 8px' : '6px 12px 8px',
    transition: 'all 0.3s ease',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    boxSizing: 'border-box',
    ...(isDesktop
      ? {
          width: 'calc(100% - 24px)',
          maxWidth: '720px',
        }
      : {}),
  };
  const phaseRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '4px',
    fontSize: '14px',
    fontWeight: 700,
  };
  const progressOuterStyle = {
    marginTop: '8px',
    width: '100%',
    height: '8px',
    borderRadius: '999px',
    backgroundColor: '#fde68a',
    overflow: 'hidden',
  };
  const progressInnerStyle = {
    width: `${timerInfo.progressRatio * 100}%`,
    height: '100%',
    borderRadius: 'inherit',
    background: getProgressColor(timerEmphasisLevel),
    transition: 'width 0.2s linear, background-color 0.2s ease',
  };
  const referenceStyle = {
    marginTop: timerInfo.message ? '2px' : 0,
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1.35,
    color: '#6b7280',
  };

  return (
    <div style={containerStyle}>
      {title && <div style={titleStyle}>{title}</div>}

      <div style={cardStyle}>
        <PhaseStepper event={event} roomDisplayPhase={effectiveDisplayPhase} isDesktop={isDesktop} />

        <div style={phaseRowStyle}>
          <span>{meta.icon}</span>
          <span>現在: {meta.label}</span>
        </div>

        {timerInfo.message && <div style={timerStyle}>{timerInfo.message}</div>}
        {timerInfo.referenceMessage && <div style={referenceStyle}>{timerInfo.referenceMessage}</div>}

        {shouldShowProgress && (
          <div style={progressOuterStyle}>
            <div style={progressInnerStyle} />
          </div>
        )}
      </div>
    </div>
  );
}

export default RoundPhase;
